import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import type { CourseRecord, LeagueMemberRecord, ScoreRecord } from '../api/types.js';
import { useAppState } from './AppState.js';
import { EmptyState, ErrorPanel, LoadingPanel, PageIntro } from './components.js';
import { mapError } from './errors.js';
import { useLeagueMembers, useRequiredLeagueId, useRequiredMatchId, useSyncLeagueFromRoute } from './hooks.js';
import { holeNumbers, normalizeHoleValues } from '../lib/courseScorecard.js';
import { formatOneDecimal, formatWholeNumber, sumNumbers } from '../lib/formatting.js';
import {
  assignMatchStrokesForView,
  calculateCourseAndPlayingHandicapForView,
  calculateOverallNetPointsForView,
  calculatePerHolePointsForView,
  isHammerHoleForView,
} from '../lib/viewScoring.js';

function memberName(playerId: string, members: LeagueMemberRecord[]): string {
  return members.find((candidate) => candidate.player_id === playerId)?.player?.name || playerId;
}

function resolveCourseHandicapValue(
  score: ScoreRecord | null,
  handicapIndex: number,
  course: CourseRecord | null,
): number | null {
  if (score && Number.isFinite(Number(score.course_handicap))) {
    return Math.round(Number(score.course_handicap));
  }
  if (!course || !Number.isFinite(handicapIndex)) {
    return null;
  }
  return calculateCourseAndPlayingHandicapForView(handicapIndex, course).courseHandicapValue;
}

function handicapMeta(handicapIndex: number | null, courseHandicap: number | null): string {
  const parts: string[] = [];
  if (typeof handicapIndex === 'number' && Number.isFinite(handicapIndex)) {
    parts.push(`HI ${formatOneDecimal(handicapIndex)}`);
  }
  if (typeof courseHandicap === 'number' && Number.isFinite(courseHandicap)) {
    parts.push(`CH ${formatWholeNumber(courseHandicap)}`);
  }
  return parts.join(' | ');
}

export function MatchDetailPage() {
  const leagueId = useRequiredLeagueId();
  const matchId = useRequiredMatchId();
  useSyncLeagueFromRoute(leagueId);
  const { client } = useAppState();

  const matchQuery = useQuery({
    queryKey: ['match', leagueId, matchId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getMatch(leagueId, matchId)).match,
  });
  const membersQuery = useLeagueMembers(leagueId);
  const coursesQuery = useQuery({
    queryKey: ['courses', leagueId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getCourses(leagueId)).courses,
  });
  const matchDayQuery = useQuery({
    queryKey: ['match-day-for-match', leagueId, matchQuery.data?.match_day_id ?? null],
    enabled: Boolean(client && matchQuery.data?.match_day_id),
    queryFn: async () => (await client!.getMatchDay(leagueId, matchQuery.data!.match_day_id)).matchDay,
  });
  const scoresQuery = useQuery({
    queryKey: ['match-scores', leagueId, matchId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getMatchScores(leagueId, matchId)).scores,
  });
  const seasonPlayersQuery = useQuery({
    queryKey: ['seasonPlayers', leagueId, matchQuery.data?.season_id ?? null],
    enabled: Boolean(client && matchQuery.data?.season_id),
    queryFn: async () => (await client!.getSeasonPlayers(leagueId, matchQuery.data!.season_id)).seasonPlayers,
  });

  if (
    matchQuery.isLoading ||
    membersQuery.isLoading ||
    coursesQuery.isLoading ||
    scoresQuery.isLoading ||
    matchDayQuery.isLoading ||
    seasonPlayersQuery.isLoading
  ) {
    return <LoadingPanel title="Match Detail" message="Loading match." />;
  }

  if (
    matchQuery.isError ||
    membersQuery.isError ||
    coursesQuery.isError ||
    scoresQuery.isError ||
    matchDayQuery.isError ||
    seasonPlayersQuery.isError ||
    !matchQuery.data
  ) {
    return (
      <ErrorPanel
        title="Match Detail"
        message={mapError(
          matchQuery.error ??
            membersQuery.error ??
            coursesQuery.error ??
            scoresQuery.error ??
            matchDayQuery.error ??
            seasonPlayersQuery.error,
        )}
      />
    );
  }

  const match = matchQuery.data;
  const members = membersQuery.data ?? [];
  const matchDay = matchDayQuery.data ?? null;
  const course = (coursesQuery.data ?? []).find((candidate) => candidate.id === match.course_id) ?? null;
  const scoreA = (scoresQuery.data ?? []).find((score) => score.player_id === match.player_a_id) ?? null;
  const scoreB = (scoresQuery.data ?? []).find((score) => score.player_id === match.player_b_id) ?? null;
  const seasonPlayers = seasonPlayersQuery.data ?? [];
  const seasonPlayerA = seasonPlayers.find((seasonPlayer) => seasonPlayer.player_id === match.player_a_id) ?? null;
  const seasonPlayerB = seasonPlayers.find((seasonPlayer) => seasonPlayer.player_id === match.player_b_id) ?? null;
  const hammerHole = Number.isInteger(matchDay?.hammer_hole) ? Number(matchDay?.hammer_hole) : null;
  const playerAName = memberName(match.player_a_id, members);
  const playerBName = memberName(match.player_b_id, members);
  const handicapIndexA = Number(
    scoreA?.handicap_index ?? seasonPlayerA?.current_handicap_index ?? seasonPlayerA?.starting_handicap ?? Number.NaN,
  );
  const handicapIndexB = Number(
    scoreB?.handicap_index ?? seasonPlayerB?.current_handicap_index ?? seasonPlayerB?.starting_handicap ?? Number.NaN,
  );
  const courseHandicapA = resolveCourseHandicapValue(scoreA, handicapIndexA, course);
  const courseHandicapB = resolveCourseHandicapValue(scoreB, handicapIndexB, course);
  const scoredMatch = Boolean(scoreA && scoreB && course);
  const blankPreview = Boolean(
    !scoredMatch &&
      course &&
      typeof courseHandicapA === 'number' &&
      Number.isFinite(courseHandicapA) &&
      typeof courseHandicapB === 'number' &&
      Number.isFinite(courseHandicapB),
  );
  const previewStrokes = blankPreview && course
    ? assignMatchStrokesForView(
        match.player_a_id,
        courseHandicapA!,
        match.player_b_id,
        courseHandicapB!,
        course.hole_handicaps,
      )
    : null;

  const grossA = scoredMatch ? normalizeHoleValues(scoreA?.hole_scores ?? [], Number.NaN) : [];
  const grossB = scoredMatch ? normalizeHoleValues(scoreB?.hole_scores ?? [], Number.NaN) : [];
  const strokesA = scoredMatch
    ? normalizeHoleValues(scoreA?.match_strokes ?? [], 0)
    : normalizeHoleValues(previewStrokes?.[match.player_a_id] ?? [], 0);
  const strokesB = scoredMatch
    ? normalizeHoleValues(scoreB?.match_strokes ?? [], 0)
    : normalizeHoleValues(previewStrokes?.[match.player_b_id] ?? [], 0);
  const netA = scoredMatch ? grossA.map((value, index) => (Number.isFinite(value) ? value - strokesA[index]! : Number.NaN)) : [];
  const netB = scoredMatch ? grossB.map((value, index) => (Number.isFinite(value) ? value - strokesB[index]! : Number.NaN)) : [];
  const perHolePoints = scoredMatch ? calculatePerHolePointsForView(netA, netB, hammerHole) : { pointsA: [], pointsB: [] };
  const overallPoints = scoredMatch
    ? calculateOverallNetPointsForView(Number(scoreA?.match_net_score), Number(scoreB?.match_net_score))
    : null;
  const matchTitle =
    match.status === 'completed'
      ? `${playerAName} (${match.player_a_points}) vs. ${playerBName} (${match.player_b_points})`
      : `${playerAName} vs. ${playerBName}`;

  return (
    <>
      <PageIntro
        title={matchTitle}
        actions={
          <Link className="nav-link" to={`/leagues/${leagueId}/matches`}>
            Back to schedule
          </Link>
        }
      />
      <section className="grid">
        <article className="panel stack">
          
          {!course ? (
            <EmptyState title="Course TBD" message="" />
          ) : scoredMatch ? (
            <>
              <MatchScorecardView
                mode="scored"
                course={course}
                hammerHole={hammerHole}
                playerName={playerAName}
                handicapIndex={handicapIndexA}
                courseHandicap={courseHandicapA}
                gross={grossA}
                grossTotal={scoreA?.gross_score ?? null}
                strokes={strokesA}
                net={netA}
                netTotal={scoreA?.match_net_score ?? null}
                holePoints={perHolePoints.pointsA}
                totalPoints={match.player_a_points}
                overallPoints={overallPoints?.pointsA ?? null}
              />
              <MatchScorecardView
                mode="scored"
                course={course}
                hammerHole={hammerHole}
                playerName={playerBName}
                handicapIndex={handicapIndexB}
                courseHandicap={courseHandicapB}
                gross={grossB}
                grossTotal={scoreB?.gross_score ?? null}
                strokes={strokesB}
                net={netB}
                netTotal={scoreB?.match_net_score ?? null}
                holePoints={perHolePoints.pointsB}
                totalPoints={match.player_b_points}
                overallPoints={overallPoints?.pointsB ?? null}
              />
            </>
          ) : blankPreview ? (
            <>
              <MatchScorecardView
                mode="blank"
                course={course}
                hammerHole={hammerHole}
                playerName={playerAName}
                handicapIndex={handicapIndexA}
                courseHandicap={courseHandicapA}
                gross={[]}
                grossTotal={null}
                strokes={strokesA}
                net={[]}
                netTotal={null}
                holePoints={[]}
                totalPoints={null}
                overallPoints={null}
              />
              <MatchScorecardView
                mode="blank"
                course={course}
                hammerHole={hammerHole}
                playerName={playerBName}
                handicapIndex={handicapIndexB}
                courseHandicap={courseHandicapB}
                gross={[]}
                grossTotal={null}
                strokes={strokesB}
                net={[]}
                netTotal={null}
                holePoints={[]}
                totalPoints={null}
                overallPoints={null}
              />
            </>
          ) : (
            <EmptyState title={match.status === 'scheduled' ? 'Scorecard unavailable' : 'Scores unavailable'} message="" />
          )}
        </article>
      </section>
    </>
  );
}

function grossSymbolClass(score: number, par: number): string {
  const delta = score - par;
  if (delta <= -2) return 'sym-double-circle';
  if (delta === -1) return 'sym-circle';
  if (delta === 1) return 'sym-square';
  if (delta >= 2) return 'sym-double-square';
  return '';
}

function MatchScorecardView({
  mode,
  course,
  hammerHole,
  playerName,
  handicapIndex,
  courseHandicap,
  gross,
  grossTotal,
  strokes,
  net,
  netTotal,
  holePoints,
  totalPoints,
  overallPoints,
}: {
  mode: 'scored' | 'blank';
  course: CourseRecord;
  hammerHole: number | null;
  playerName: string;
  handicapIndex: number | null;
  courseHandicap: number | null;
  gross: number[];
  grossTotal: number | null;
  strokes: number[];
  net: number[];
  netTotal: number | null;
  holePoints: number[];
  totalPoints: number | null;
  overallPoints: number | null;
}) {
  const holes = holeNumbers();
  const meta = handicapMeta(handicapIndex, courseHandicap);

  return (
    <section className="match-detail-scorecard">
      <header className="match-detail-player-header">
        <div>
          <h3 className="match-detail-player-name">{playerName}</h3>
          {meta ? <p className="match-detail-player-meta">{meta}</p> : null}
        </div>
        {mode === 'scored' ? (
          <div className="match-detail-totals">
            <span>Gross <strong>{grossTotal ?? '-'}</strong></span>
            <span>Net <strong>{netTotal ?? '-'}</strong></span>
            <span>Pts <strong>{totalPoints ?? '-'}</strong></span>
          </div>
        ) : null}
      </header>
      <div className="match-detail-table-wrap">
        <table className="match-detail-table">
          <thead>
            <tr>
              <th></th>
              {holes.map((hole) => (
                <th key={hole} className={isHammerHoleForView(hole, hammerHole) ? 'hammer-col' : ''}>
                  {isHammerHoleForView(hole, hammerHole) ? <span className="hammer-marker">Hammer</span> : null}
                  {hole}
                </th>
              ))}
              <th className="total-col">TOT</th>
            </tr>
          </thead>
          <tbody>
            <tr className="row-reference">
              <td>Par</td>
              {holes.map((hole, index) => (
                <td key={`par-${hole}`} className={isHammerHoleForView(hole, hammerHole) ? 'hammer-col' : ''}>
                  <span className="match-detail-reference-value">{course.hole_pars[index]}</span>
                </td>
              ))}
              <td className="total-col">{course.par}</td>
            </tr>
            <tr className="row-reference">
              <td>Hcp</td>
              {holes.map((hole, index) => (
                <td key={`handicap-${hole}`} className={isHammerHoleForView(hole, hammerHole) ? 'hammer-col' : ''}>
                  <span className="match-detail-reference-value">{course.hole_handicaps[index]}</span>
                </td>
              ))}
              <td className="total-col">-</td>
            </tr>
            <tr className="row-gross">
              <td>Gross</td>
              {holes.map((hole, index) => (
                <td key={`gross-${hole}`} className={isHammerHoleForView(hole, hammerHole) ? 'hammer-col' : ''}>
                  {mode === 'scored' && Number.isFinite(gross[index]) ? (
                    <span className={`gross-score-symbol ${grossSymbolClass(gross[index]!, course.hole_pars[index]!)}`}>
                      {gross[index]}
                    </span>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </td>
              ))}
              <td className="total-col">{grossTotal ?? '-'}</td>
            </tr>
            <tr className="row-strokes">
              <td>Strokes</td>
              {holes.map((hole, index) => (
                <td key={`stroke-${hole}`} className={isHammerHoleForView(hole, hammerHole) ? 'hammer-col' : ''}>
                  {strokes[index] && strokes[index]! > 0 ? (
                    <span className="match-detail-stroke-dots">
                      {Array.from({ length: strokes[index]! }, (_value, dotIndex) => (
                        <span key={dotIndex} className="match-detail-stroke-dot"></span>
                      ))}
                    </span>
                  ) : mode === 'scored' ? (
                    <span className="match-detail-stroke-none">-</span>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </td>
              ))}
              <td className="total-col">{sumNumbers(strokes) || '-'}</td>
            </tr>
            <tr className="row-net">
              <td>Net</td>
              {holes.map((hole, index) => (
                <td key={`net-${hole}`} className={isHammerHoleForView(hole, hammerHole) ? 'hammer-col' : ''}>
                  {mode === 'scored' && Number.isFinite(net[index]) ? net[index] : <span>&nbsp;</span>}
                </td>
              ))}
              <td className="total-col">{netTotal ?? '-'}</td>
            </tr>
            {mode === 'scored' ? (
              <>
                <tr className="row-hole-points">
                  <td>Hole Pts</td>
                  {holes.map((hole, index) => (
                    <td key={`points-${hole}`} className={isHammerHoleForView(hole, hammerHole) ? 'hammer-col' : ''}>
                      <span
                        className={`match-detail-points-pill ${
                          holePoints[index]! <= 0
                            ? 'zero'
                            : isHammerHoleForView(hole, hammerHole) && holePoints[index] === 4
                              ? 'hammer-win'
                              : isHammerHoleForView(hole, hammerHole) && holePoints[index] === 2
                                ? 'hammer-half'
                                : holePoints[index] === 1
                                  ? 'half'
                                  : 'win'
                        }`}
                      >
                        {holePoints[index]! <= 0 ? '-' : holePoints[index]}
                      </span>
                    </td>
                  ))}
                  <td className="total-col">{sumNumbers(holePoints)}</td>
                </tr>
                <tr className="row-net-points">
                  <td>Net Pts</td>
                  <td colSpan={holes.length}></td>
                  <td className="total-col">
                    <span className={`match-detail-overall-pill ${overallPoints! <= 0 ? 'zero' : overallPoints === 2 ? 'half' : 'win'}`}>
                      {overallPoints}
                    </span>
                  </td>
                </tr>
              </>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}
